import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import type { Account } from "../lib/types";

type LinkContext = { accountNumber: string; purpose: string; expiresAt: string; mfaRequired: boolean };

export function PasswordForm({ account, token, context, onSuccess }: {
  account?: Account; token?: string; context?: LinkContext; onSuccess: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordRepeat, setNewPasswordRepeat] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mfaRequired = context?.mfaRequired || account?.roles.includes("ADMIN");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (newPassword !== newPasswordRepeat) { setError("Die neuen Passwörter stimmen nicht überein."); return; }
    if (newPassword.length < 8 || new TextEncoder().encode(newPassword).length > 72) {
      setError("Das Passwort benötigt mindestens 8 Zeichen und darf höchstens 72 UTF-8-Bytes lang sein."); return;
    }
    setBusy(true);
    try {
      await api(token ? "/auth/password-link/complete" : "/auth/password/change", { method: "POST", body: JSON.stringify({
        ...(token ? { token } : { currentPassword }), newPassword, newPasswordRepeat, ...(mfaRequired ? { mfaCode } : {}),
      }) });
      setCurrentPassword(""); setNewPassword(""); setNewPasswordRepeat(""); setMfaCode("");
      onSuccess();
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Das Passwort konnte nicht geändert werden."); }
    finally { setBusy(false); }
  }
  return <form className="password-form compact-form" onSubmit={submit}>
    <p className="eyebrow">{token ? "Calepia" : "Mein Konto"}</p>
    <h1>{context?.purpose === "INITIAL" ? "Konto einrichten" : "Passwort ändern"}</h1>
    <p>Kontonummer: <strong>{context?.accountNumber ?? account?.accountNumber}</strong></p>
    {!token && <label>Aktuelles Passwort<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>}
    <label>Neues Passwort<input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={8} required /></label>
    <label>Neues Passwort wiederholen<input type="password" autoComplete="new-password" value={newPasswordRepeat} onChange={(event) => setNewPasswordRepeat(event.target.value)} required /></label>
    <small>Mindestens 8 Zeichen, höchstens 72 UTF-8-Bytes. Leerzeichen bleiben Bestandteil des Passworts.</small>
    {mfaRequired && <label>Authenticator- oder Recoverycode<input autoComplete="one-time-code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} required maxLength={40} /></label>}
    {context && <p>Link gültig bis {new Date(context.expiresAt).toLocaleString("de-DE")}.</p>}
    {!token && <p>Nach der Änderung werden Sie auf allen Geräten abgemeldet. Ihr QR-Zugang bleibt erhalten.</p>}
    {error && <p role="alert" className="alert">{error}</p>}
    <button className="primary" type="submit" disabled={busy}>{busy ? "Wird gespeichert …" : "Passwort speichern"}</button>
  </form>;
}

export function PasswordLinkView({ token, onClose }: { token: string; onClose: () => void }) {
  const [context, setContext] = useState<LinkContext | null>(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    api<LinkContext>("/auth/password-link/context", { method: "POST", body: JSON.stringify({ token }) })
      .then(setContext).catch((failure) => setError(failure.message));
  }, [token]);
  return <main className="password-link-page"><section className="password-card">
    <img className="password-logo" src="/brand/calepia_logo_schrift.webp" alt="Calepia" />
    {done ? <><h1>Passwort gespeichert</h1><p>Ihr Passwort wurde gespeichert. Melden Sie sich jetzt regulär an.</p><button className="primary" onClick={onClose}>Zur Anmeldung</button></>
      : error ? <><h1>Link nicht verfügbar</h1><p role="alert">{error}</p><button className="secondary" onClick={onClose}>Zur Anmeldung</button></>
      : context ? <PasswordForm token={token} context={context} onSuccess={() => setDone(true)} /> : <p>Link wird geprüft …</p>}
  </section></main>;
}
